import { capitalize, first, get, startCase, upperCase } from 'lodash'

import { DEFAULT_CHART_BG_COLOR } from './Liveboard.constant'

const convertToCapital = (string: string) => {
  return startCase(capitalize(upperCase(string)))
}

import moment from 'moment'

export const generateLabelsFromDates = (
  datePicked?: any[],
  currentPeriod?: string
) => {
  if (
    !datePicked ||
    datePicked.length !== 2 ||
    !datePicked[0] ||
    !datePicked[1] ||
    !currentPeriod
  )
    return null

  const labels = []

  let start = moment(datePicked[0])
  let end = moment(datePicked[1])
  let unit: any = 'month'
  let formatString = 'YYYY-MMM'
  let isUpperCase = true

  switch (currentPeriod.toLowerCase()) {
    case 'daily':
      unit = 'day'
      start = start.startOf('day')
      end = end.startOf('day')
      formatString = 'YYYY-MM-DD'
      isUpperCase = false
      break
    case 'weekly':
      unit = 'week'
      start = start.startOf('isoWeek')
      end = end.startOf('isoWeek')
      formatString = 'GGGG [W]WW'
      isUpperCase = false
      break
    case 'monthly':
      unit = 'month'
      start = start.startOf('month')
      end = end.startOf('month')
      formatString = 'YYYY-MMM'
      isUpperCase = true
      break
    case 'quarterly':
      unit = 'quarter'
      start = start.startOf('quarter')
      end = end.startOf('quarter')
      formatString = 'YYYY-[Q]Q'
      isUpperCase = false
      break
    case 'yearly':
      unit = 'year'
      start = start.startOf('year')
      end = end.startOf('year')
      formatString = 'YYYY'
      isUpperCase = false
      break
    default:
      return null
  }

  const current = start.clone()
  while (current.isSameOrBefore(end)) {
    const formatted = current.format(formatString)
    labels.push(isUpperCase ? formatted.toUpperCase() : formatted)
    current.add(1, unit)
  }

  return labels.length > 0 ? labels : null
}

export const transformTwoColumnChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) => item.display_name)
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )
  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((item) => item[index + 1]),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`
      ),
      datalabels: {
        color: 'rgb(255, 255, 255)',
      },
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformLineChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => first(item))
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )
  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      hidden: false,
      fill: false,
      data: get(data, 'rows', []).map((row) => row[index + 1]),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ),
      datalabels: {
        display: true,
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          ''
        ),
      },
      tension: 0.5,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformHundredPercentStackedColumnChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((item) => item[index + 1]),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        ''
      ),
      datalabels: {
        color: 'rgb(255, 255, 255)',
      },
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformColumnChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      datalabels: {
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          DEFAULT_CHART_BG_COLOR
        ),
      },
      borderRadius: 10,
    }
  })

  return {
    labels,
    datasets,
  }
}

export const transformColumnAndLineChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      type: index === 1 ? 'bar' : 'line',
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`).toFixed(0)
      }),

      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      datalabels: {
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          DEFAULT_CHART_BG_COLOR
        ),
      },
      borderRadius: 10,
      tension: 0.5,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformBarChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => convertToCapital(item[0]))
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      type: 'bar',
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),

      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      datalabels: {
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          DEFAULT_CHART_BG_COLOR
        ),
      },
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformGaugeChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = [get(valueToShow, '[1]')].filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),

      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      datalabels: {
        color: get(
          chartItem,
          `configure.options.backgroundColor[${index}]`,
          DEFAULT_CHART_BG_COLOR
        ),
      },
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformAreaChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      tension: 0.5,
      fill: true,
      backgroundColor: get(
        chartItem,
        `configure.options.bgColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformHundredPercentStakeBarChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      tension: 0.5,
      fill: true,
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(131, 131, 131, 1)'
      ),
      datalabels: {
        color: 'rgba(255, 255, 255, 1)',
      },
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformHundredPercentStakeBarGlassonsChart = (
  data,
  chartItem
) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )

  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    const myData = get(data, 'rows', []).map((row) => {
      return get(row, `[${index + 1}]`)
    })
    const backgroundColor = get(
      chartItem,
      `configure.options.backgroundColor[${index}]`,
      'rgba(132, 132, 132, 0.3)'
    )

    const borderColor = get(
      chartItem,
      `configure.options.backgroundColor[${index}]`,
      'rgba(131, 131, 131, 1)'
    )

    return {
      label: item,
      data: myData,
      tension: 0.5,
      fill: true,
      backgroundColor,
      borderColor,
      datalabels: {
        color: 'rgba(255, 255, 255, 1)',
      },
      borderRadius: 10,
      // yAxisID: item,
    }
  })

  return {
    labels,
    datasets,
  }
}

export const transformDoughnutChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformPieChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformTwoBarChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderRadius: 10,
    }
  })
  return {
    labels,
    datasets,
  }
}

export const transformMultiChart = (data, chartItem) => {
  const labels = get(data, 'rows', []).map((item) => item[0])
  const valueToShow = get(data, 'cols', []).map((item) =>
    get(item, 'display_name', '')
  )
  const labelDataSet = valueToShow.filter((element) =>
    get(chartItem, 'configure.chart_value', []).includes(element)
  )

  const datasets = labelDataSet?.map((item, index) => {
    return {
      label: item,
      data: get(data, 'rows', []).map((row) => {
        return get(row, `[${index + 1}]`)
      }),
      backgroundColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        'rgba(132, 132, 132, 0.3)'
      ),
      borderColor: get(
        chartItem,
        `configure.options.backgroundColor[${index}]`,
        DEFAULT_CHART_BG_COLOR
      ),
      fill: true,
      tension: 0.5,
    }
  })
  return {
    labels,
    datasets,
  }
}
